/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import React, {
  memo,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react'
import { useParams, useLocation } from 'react-router-dom'
import { App, Spin, FloatButton } from 'antd'
import { DeleteOutlined, ArrowUpOutlined } from '@ant-design/icons'
import useSocialFeed from '@sportninja/common/hooks/useSocialFeed'
import FeedItem from '../FeedItem'
import colors from '@sportninja/common/constants/appColors'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import Text from '../ts-components/Text'
import DeleteModal from '../Modal/DeleteModal'
import ReportModal from '../ReportModal'
import { User } from '@sportninja/common/types'
import {
  getHighQualityImageThumbnailId,
  getImageThumbnailId,
} from '@sportninja/common/reducers/helpers'

const SCROLL_THRESHOLD = 500

// Extract styled components to prevent recreation on every render
const deleteButtonStyles = css`
  display: flex;
  padding: 8px 16px 8px 8px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 8px;
  border: 1px solid ${colors.ERROR_200};
  box-shadow: 0px 2px 0px 0px rgba(5, 145, 255, 0.1);
  font-family: ${isCanlan
    ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif !important;'
    : 'BarlowCondensed, Arial, Helvetica, sans-serif !important;'};
  display: none;
`

const deleteIconStyles = css`
  color: ${colors.ERROR_200};
`

const feedPageStyles = css`
  width: 100%;
  margin: 0 auto;
`

const emptyFeedContainerStyles = css`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
`

const emptyFeedImageStyles = css`
  width: 180px;
  height: 160px;
`

// Memoized DeleteButton component
const DeleteButton = memo<React.ButtonHTMLAttributes<HTMLButtonElement>>(
  ({ ...props }) => {
    return (
      <button id='delete-post-button' css={deleteButtonStyles} {...props}>
        <DeleteOutlined css={deleteIconStyles} width={24} height={24} />
        <Text variant='paragraphMedium' weight='semiBold' color={colors.WHITE}>
          DELETE POST
        </Text>
      </button>
    )
  }
)

DeleteButton.displayName = 'DeleteButton'

// Improved interface with better type definitions
interface FeedPageProps {
  entityType?: 'team' | 'schedule' | 'organization'
  entityId?: string
  canEdit?: boolean
  canDelete?: boolean
  avatarUrl?: string
  refreshNewFeed: React.MutableRefObject<(() => void) | null>
  setPermission?: (permission: any) => void
  user: User
  isEntityFeed?: boolean
  setPostToEdit: (post: any) => void
  setModalOpen: (open: boolean) => void
  setIsLoadingFeed: (loading: boolean) => void
  setIsModalOpen: (open: boolean) => void
  isModalOpen?: boolean
}

// Memoized empty feed content
const EmptyFeedContent = memo(() => (
  <div css={emptyFeedContainerStyles}>
    <img
      src='/images/empty_feed.png'
      alt='Empty Feed'
      css={emptyFeedImageStyles}
    />
    <Text variant='heading5' weight='semiBold' color={colors.NEUTRAL_0}>
      Nothing to see here yet
    </Text>
    <Text variant='paragraphSmall' weight='regular' color={colors.NEUTRAL_50}>
      Be the first to post and get things started!
    </Text>
  </div>
))

EmptyFeedContent.displayName = 'EmptyFeedContent'

const FeedPage: React.FC<FeedPageProps> = ({
  entityType,
  entityId,
  canEdit = false,
  canDelete = false,
  avatarUrl,
  refreshNewFeed,
  setPermission,
  user,
  setPostToEdit,
  setModalOpen,
  setIsLoadingFeed,
  setIsModalOpen,
  isModalOpen = false,
}) => {
  const { message } = App.useApp()
  const params = useParams<{ id?: string }>()
  const location = useLocation()
  const [postId, setPostId] = useState<string | null>(null)
  const [isReportModalOpen, setIsReportModalOpen] = useState(false)
  const [reportingPostId, setReportingPostId] = useState<string | null>(null)
  const [targetPostId, setTargetPostId] = useState<string | null>(null)
  const [hasProcessedFeedParam, setHasProcessedFeedParam] = useState(false)

  // Ref to prevent multiple simultaneous loadMore calls
  const isLoadingMoreRef = useRef(false)

  const {
    posts,
    loading,
    error,
    hasMore,
    likePost,
    dislikePost,
    deletePost,
    createReply,
    loadMore,
    fetchPosts,
    permission,
  } = useSocialFeed({ id: entityId, type: entityType })

  // Memoize user ID to prevent unnecessary re-renders
  const userId = useMemo(() => user?.id, [user?.id])

  // Get feed ID from URL path parameter
  const feedId = useMemo(() => {
    return params.id
  }, [params.id])

  // Get analytics type from query parameter with 'social_feed' as default
  const analyticsType = useMemo(() => {
    const searchParams = new URLSearchParams(location.search)
    const type = searchParams.get('type')

    // Validate the type parameter
    if (type === 'email' || type === 'push') {
      return type
    }

    // Default to 'social_feed'
    return 'social_feed'
  }, [location.search])

  // Sync loading state
  useEffect(() => {
    setIsLoadingFeed(loading)
  }, [loading, setIsLoadingFeed])

  // Sync permission state
  useEffect(() => {
    setPermission?.(permission)
  }, [permission, setPermission])

  // Set refresh function reference
  useEffect(() => {
    refreshNewFeed.current = () => fetchPosts(1)
  }, [fetchPosts, refreshNewFeed])

  // Handle errors
  useEffect(() => {
    if (error) {
      message.error(error)
    }
  }, [error, message])

  // Handle feed ID from URL path to open specific post modal
  useEffect(() => {
    if (feedId && !loading && !hasProcessedFeedParam && posts.length > 0) {
      const targetPost = posts.find((post) => post.id === feedId)
      if (targetPost) {
        setTargetPostId(feedId)
        setHasProcessedFeedParam(true)
      }
    }
  }, [feedId, loading, hasProcessedFeedParam, posts])

  // Memoized event handlers to prevent unnecessary re-renders
  const handleLikePost = useCallback(
    async (postId: string) => {
      try {
        await likePost(postId)
      } catch (err) {
        console.error('Failed to like post:', err)
        message.error('Failed to like post. Please try again.')
      }
    },
    [likePost, message]
  )

  const handleDislikePost = useCallback(
    async (likeId: string) => {
      try {
        await dislikePost(likeId)
      } catch (err) {
        console.error('Failed to dislike post:', err)
        message.error('Failed to dislike post. Please try again.')
      }
    },
    [dislikePost, message]
  )

  const handleCreateReply = useCallback(
    async (replyId: string, replyContent: string) => {
      if (!replyContent.trim()) {
        return
      }

      try {
        await createReply(replyId, { body: replyContent })
        message.success('Reply posted successfully')
      } catch (err) {
        console.error('Failed to create reply:', err)
        message.error('Failed to post reply. Please try again.')
      }
    },
    [createReply, message]
  )

  const handleReportPost = useCallback((reportId: string) => {
    setReportingPostId(reportId)
    setIsReportModalOpen(true)
  }, [])

  const handleSubmitReport = useCallback(async () => {
    try {
      message.success('Post Reported Successfully')
      setReportingPostId(null)
      setIsReportModalOpen(false)
    } catch (err) {
      console.error('Failed to report post:', err)
      message.error('Failed to report post. Please try again.')
    }
  }, [message])

  const handleCloseReportModal = useCallback(() => {
    setIsReportModalOpen(false)
    setReportingPostId(null)
  }, [])

  const handleDeletePost = useCallback(
    async (shouldForce = false) => {
      if (!postId) {
        throw new Error('No post ID provided')
      }

      try {
        const query: any = {}
        if (shouldForce) {
          query.force = 1
        }
        await deletePost(postId)
        message.success('Post deleted successfully.')
        fetchPosts(1)
        setPostId(null)
      } catch (err: any) {
        console.error('Failed to delete post:', err)
        message.error(err?.message || 'Failed to delete post')
        throw err
      }
    },
    [postId, deletePost, message, fetchPosts]
  )

  const handleDeleteClick = useCallback((deleteId: string) => {
    setPostId(deleteId)
    // Use a more reliable method to trigger the delete modal
    setTimeout(() => {
      document.getElementById('delete-post-button')?.click()
    }, 0)
  }, [])

  const handleEditClick = useCallback(
    (post: any) => {
      setPostToEdit(post)
      setModalOpen(true)
    },
    [setPostToEdit, setModalOpen]
  )

  const handleLikeToggle = useCallback(
    (post: any) => {
      return post.is_liked
        ? handleDislikePost(post.id)
        : handleLikePost(post.id)
    },
    [handleLikePost, handleDislikePost]
  )

  // Memoize permission checks to avoid recalculating on every render
  const getPostPermissions = useCallback(
    (post: any) => {
      const isOwner = post?.user_id === userId
      const hasUpdatePermission = permission?.[post?.parent_id]?.update
      const hasDeletePermission = permission?.[post?.parent_id]?.delete
      const hasAdminPermission = permission?.[post?.parent_id]?.admin

      return {
        canEdit:
          canEdit || isOwner || hasUpdatePermission || hasAdminPermission,
        canDelete:
          canDelete || isOwner || hasDeletePermission || hasAdminPermission,
      }
    },
    [canEdit, canDelete, userId, permission]
  )

  const handleScroll = useCallback(() => {
    if (loading || isLoadingMoreRef.current || isModalOpen) {
      return
    }

    const { scrollTop, scrollHeight, clientHeight } = document.documentElement
    const nearBottom =
      scrollHeight - (scrollTop + clientHeight) < SCROLL_THRESHOLD
    if (nearBottom && hasMore) {
      isLoadingMoreRef.current = true
      loadMore()
    }
  }, [hasMore, loading, loadMore, isModalOpen])

  useEffect(() => {
    if (!loading) {
      isLoadingMoreRef.current = false
    }
  }, [loading])

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [handleScroll])

  // Show empty state when no posts and not loading
  const showEmptyState = posts.length === 0 && !loading

  return (
    <>
      <DeleteModal
        title='Delete Post'
        message='Are you sure you want to delete this post? This cannot be undone.'
        onDelete={handleDeletePost}
        Button={DeleteButton}
        forceDeleteText={undefined}
        shouldCloseModalAfterDelete={true}
        useForceDelete={true}
        displayBanner={false}
      />

      <ReportModal
        isOpen={isReportModalOpen}
        onClose={handleCloseReportModal}
        onReport={handleSubmitReport}
        entityType='post'
        entityId={reportingPostId || undefined}
      />

      <div id='feed-page' css={feedPageStyles}>
        <Spin
          spinning={loading && posts.length === 0}
          size='small'
          css={css`
            margin-top: 24px;
          `}
        >
          <div id='feed-page-content'>
            {showEmptyState && <EmptyFeedContent />}

            {posts.map((post) => {
              const { canEdit: postCanEdit, canDelete: postCanDelete } =
                getPostPermissions(post)

              return (
                <FeedItem
                  role_types={post.role_types}
                  sendSocialFeed={post.send_social_feed}
                  sendEmail={post.send_email}
                  sendPushNotification={post.send_push_notification}
                  key={post.id}
                  userProfilePicture={user?.imageId}
                  userName={post?.user?.name_full}
                  userId={post?.user?.id}
                  entityId={post?.parent_id || ''}
                  entityType={
                    post?.parent_type === 'organization'
                      ? 'organization'
                      : post?.parent_type === 'team'
                      ? 'team'
                      : 'schedule'
                  }
                  entityName={post?.parent?.name}
                  title={post?.title}
                  body={post?.body}
                  imageUrl={getHighQualityImageThumbnailId(post)}
                  linkUrl={post?.url}
                  likesCount={post?.like_count || 0}
                  commentsCount={post?.replies?.length || 0}
                  comments={post?.replies || []}
                  createdAt={post.created_at}
                  avatarUrl={getImageThumbnailId(post?.parent)}
                  onCommentClick={() => {
                    /* Open comment input or modal */
                  }}
                  onReplySubmit={(replyContent) =>
                    handleCreateReply(post.id, replyContent)
                  }
                  onLikeClick={() => handleLikeToggle(post)}
                  onReportClick={() => handleReportPost(post.id)}
                  onDeleteClick={() => handleDeleteClick(post.id)}
                  onEditClick={() => handleEditClick(post)}
                  canEdit={postCanEdit}
                  canDelete={postCanDelete}
                  isAppPost={post?.parent === null || post?.is_global}
                  userAvatarUrl={avatarUrl}
                  isLiked={post.is_liked}
                  postId={post.id}
                  selfUserId={userId}
                  selfUserName={user?.name}
                  fetchPosts={fetchPosts}
                  setIsModalOpen={setIsModalOpen}
                  canReply={(post as any).can_reply !== false}
                  shouldAutoOpenModal={targetPostId === post.id}
                  onModalAutoOpened={() => setTargetPostId(null)}
                  analyticsType={analyticsType}
                />
              )
            })}

            {loading && posts.length > 0 && (
              <div
                css={css`
                  display: flex;
                  justify-content: center;
                  padding: 20px;
                `}
              >
                <Spin size='small' />
              </div>
            )}
          </div>
        </Spin>
      </div>
      <FloatButton.BackTop
        icon={<ArrowUpOutlined />}
        css={css`
          .ant-float-btn-body {
            background-color: ${colors.PRIMARY} !important;
          }
          .ant-float-btn-icon {
            color: ${colors.NEUTRAL_0} !important;
          }
        `}
      />
    </>
  )
}

export default memo(FeedPage)
